import React, { useEffect, useLayoutEffect } from "react";
import { Badge, Box, Flex } from "@chakra-ui/react";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import { PersistedLineSheetSetListQuery } from "@/features/order-sheet-sets/agency-line-sheet-set-list-page";
import { useChipList } from "@/features/order-sheet-sets/hooks/use-chip-list";
import SVG from "react-inlinesvg";
import { SheetTabFilterQuery } from "@/features/order-sheet-sets/sheet-tab-filter";
import { PersistedLineSheetSetDetailPageQuery } from "@/features/line-sheet-sets/detail/use-persisted-line-sheet-sheet-set-detail-page-query";
import { BestSellingSectionQuery } from "@/features/dashboard/features/best-selling-section/hooks/useBsQuery";

interface Props {
  query:
    | (PersistedLineSheetSetListQuery &
        SheetTabFilterQuery &
        PersistedLineSheetSetDetailPageQuery &
        BestSellingSectionQuery)
    | any;
  setQuery: any;
  useCustomChipList?: (query: any) => any;
}

const ChipList = ({ query, setQuery, useCustomChipList }: Props) => {
  const useNewChipList = useCustomChipList ?? useChipList;
  const { chipList, refineQuery } = useNewChipList(query);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [scrollPos, setScrollPos] = React.useState<"start" | "end" | "middle">(
    "start"
  );

  useLayoutEffect(() => {
    if (chipList.length !== 0) return;
    setIsOverflowing(false);
    setScrollPos("start");
  }, [chipList.length]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const isOverflowing =
        containerRef.current.scrollWidth > containerRef.current.clientWidth;
      setIsOverflowing(isOverflowing);
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const resizeObserver = new ResizeObserver(() => {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    });
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [chipList]);

  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current;
      if (el) {
        setScrollPos(
          el.scrollLeft === 0
            ? "start"
            : el.scrollLeft === el.scrollWidth - el.clientWidth
            ? "end"
            : "middle"
        );
      }
    };
    const el = containerRef.current;
    if (el) {
      el.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (el) {
        el.removeEventListener("scroll", handleScroll);
      }
    };
  }, [chipList.length]);

  if (chipList.length === 0) return <></>;

  return (
    <Box
      background={"transparent"}
      height={"24px"}
      position={"relative"}
      overflow={"auto"}
    >
      {isOverflowing && scrollPos !== "start" && (
        <SVG
          src={"/icons-v1_1/chevron-left.svg"}
          width={"24px"}
          height={"24px"}
          cursor={"pointer"}
          style={{
            position: "absolute",
            left: "0",
            top: "50%",
            transform: "translateY(-50%)",
            background: "#FCFCFB",
            border: "1px solid #8F8F8C",
            borderRadius: "9999px",
          }}
          onClick={() => {
            if (containerRef.current) {
              // console.log(
              //   containerRef.current.scrollWidth -
              //     containerRef.current.clientWidth,
              //   "minus"
              // );
              // console.log(containerRef.current?.scrollLeft, "left");
              containerRef.current.scrollTo({
                left: containerRef.current.scrollLeft - 100,
                behavior: "smooth",
              });
            }
          }}
        />
      )}
      <Flex
        gap={"12px"}
        height={"24px"}
        ref={containerRef}
        alignItems={"center"}
        pr={"30px"}
        overflow="auto"
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {chipList.map((item: any, index: any) => (
          <Badge
            key={`${item}-${index}`}
            borderRadius="4px"
            background={"#F7F9FA"}
            color={"#1272EF"}
            textStyle={"body-sm-medium"}
            display="flex"
            gap={"2px"}
            height={"20px"}
          >
            {item.name}
            <SVG
              src={"/icons-v1_1/x.svg"}
              width={"20px"}
              height={"20px"}
              cursor={"pointer"}
              onClick={() =>
                setQuery((prev: any) => {
                  return {
                    ...prev,
                    ...refineQuery(item.type, item.id),
                    pageNumber: 1,
                  };
                })
              }
            />
          </Badge>
        ))}
      </Flex>
      {isOverflowing && scrollPos !== "end" && (
        <SVG
          src={"/icons-v1_1/chevron-right.svg"}
          width={"24px"}
          height={"24px"}
          cursor={"pointer"}
          style={{
            position: "absolute",
            right: "0",
            top: "50%",
            transform: "translateY(-50%)",
            background: "#FCFCFB",
            border: "1px solid #8F8F8C",
            borderRadius: "9999px",
          }}
          onClick={() => {
            if (containerRef.current) {
              // console.log(
              //   containerRef.current.scrollWidth -
              //     containerRef.current.clientWidth,
              //   "minus"
              // );
              // console.log(containerRef.current?.scrollLeft, "left");
              containerRef.current.scrollTo({
                left: containerRef.current.scrollLeft + 100,
                behavior: "smooth",
              });
            }
          }}
        />
      )}
    </Box>
  );
};

export default ChipList;
